import { useEffect, useId, useState, type FormEvent } from 'react'
import { ArrowRight, Check, Mail } from 'lucide-react'
import { getNewsletterAvailability, subscribeToNewsletter, type NewsletterPlacement } from '../../services/newsletter'
import { trackEvent } from '../../services/analytics'
import { shouldRenderNewsletterPlacement } from './placement'
import { newsletterErrorMessage } from './feedback'
import './newsletterSignup.css'

function currentAttribution(): Record<string, string> {
  const search = new URLSearchParams(window.location.search)
  const attribution: Record<string, string> = {}
  for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content']) {
    const value = search.get(key)
    if (value) attribution[key] = value
  }
  return attribution
}

export function NewsletterSignup({ placement, articleId }: { placement: NewsletterPlacement; articleId?: string }) {
  const id = useId().replaceAll(':', '')
  const [email, setEmail] = useState('')
  const [website, setWebsite] = useState('')
  const [consent, setConsent] = useState(false)
  const [state, setState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')
  const [message, setMessage] = useState('')
  const [availability, setAvailability] = useState<'checking' | 'available' | 'paused' | 'unknown'>('checking')
  const pathname = typeof window === 'undefined' ? '/' : window.location.pathname
  const visible = shouldRenderNewsletterPlacement(placement, pathname)

  useEffect(() => {
    if (!visible) return
    let active = true
    getNewsletterAvailability().then((accepting) => {
      if (active) setAvailability(accepting ? 'available' : 'paused')
    }).catch(() => { if (active) setAvailability('unknown') })
    return () => { active = false }
  }, [visible])

  const checkAvailability = async () => {
    setAvailability('checking')
    try { setAvailability(await getNewsletterAvailability(true) ? 'available' : 'paused') }
    catch { setAvailability('unknown') }
  }

  // The app has a global footer placement. Suppress it wherever the same page
  // already presents a higher-context signup so readers never see stacked asks.
  if (!visible) return null

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (state === 'submitting' || availability !== 'available') return
    if (!consent) { setState('error'); setMessage('Confirm that you want to receive the briefing before continuing.'); return }
    setState('submitting'); setMessage('')
    try {
      const result = await subscribeToNewsletter({
        email,
        placement,
        path: pathname,
        website,
        consent: true,
        attribution: currentAttribution(),
      })
      setEmail(''); setConsent(false); setState('success'); setMessage('Check your inbox to finish your request.')
      trackEvent('newsletter_confirmation_requested', { placement, article_id: articleId ?? '', method: 'email', delivery: result.delivery })
    } catch (error) {
      const code = typeof error === 'object' && error && 'code' in error ? String((error as { code?: unknown }).code) : ''
      setState('error')
      setMessage(newsletterErrorMessage(code))
      if (code.endsWith('failed-precondition')) setAvailability('paused')
    }
  }

  return <section className={`newsletter-signup newsletter-signup--${placement}`} aria-labelledby={`${id}-heading`} data-analytics-section={`newsletter_${placement}`}>
    <div className="newsletter-signup-copy"><Mail aria-hidden="true"/><span><small>THE OMIKINA BRIEFING</small><strong id={`${id}-heading`}>Follow what changes—and what it depends on.</strong><p>Get original analysis and Five Changes That Matter when a new edition is ready.</p></span></div>
    {availability !== 'available' ? <div className="newsletter-availability" aria-live="polite" aria-busy={availability === 'checking'}>
      <strong>{availability === 'checking' ? 'Checking email signup…' : availability === 'paused' ? 'Email signup is temporarily unavailable.' : 'We could not check email signup.'}</strong>
      <p>{state === 'error' ? message : availability === 'paused' ? 'You can read every edition on OMIKINA or follow the free RSS feed while email delivery is unavailable.' : availability === 'unknown' ? 'Check your connection and try again. You can still read the briefing here.' : 'The latest briefing is always available to read here.'}</p>
      <nav aria-label="Follow the OMIKINA briefing"><a href="/intelligence">Read the latest briefing <ArrowRight aria-hidden="true"/></a><a href="/rss/briefings.xml">Follow via RSS</a></nav>
      {availability !== 'checking' && <button type="button" onClick={() => void checkAvailability()}>Check again</button>}
    </div> : state === 'success' ? <div className="newsletter-signup-success" role="status"><Check aria-hidden="true"/><strong>{message}</strong><span>Emails begin only after you use the confirmation link. It expires in 24 hours. Allow a few minutes and check your spam folder.</span><button type="button" onClick={() => { setState('idle'); setMessage('') }}>Request another confirmation</button></div> : <form onSubmit={submit} aria-busy={state === 'submitting'}>
      <label htmlFor={`${id}-email`}>Email address</label>
      <div><input id={`${id}-email`} name="email" type="email" inputMode="email" autoComplete="email" maxLength={254} required aria-describedby={`${id}-help${state === 'error' ? ` ${id}-error` : ''}`} value={email} onChange={(event) => { setEmail(event.target.value); if (state === 'error') setState('idle') }} placeholder="you@example.com"/><button type="submit" disabled={state === 'submitting'}>{state === 'submitting' ? 'REQUESTING…' : 'REQUEST THE BRIEFING'} <ArrowRight aria-hidden="true"/></button></div>
      <label className="newsletter-honeypot" aria-hidden="true">Website<input name="website" type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(event) => setWebsite(event.target.value)}/></label>
      <label className="newsletter-consent"><input name="newsletter-consent" type="checkbox" required checked={consent} onChange={(event) => { setConsent(event.target.checked); if (state === 'error') setState('idle') }}/><span>I want OMIKINA briefing emails and accept the <a href="/privacy">privacy notice</a>.</span></label>
      <small id={`${id}-help`}>We send a confirmation email first. No briefing is sent until you confirm. No spam or data sale; unsubscribe in every edition.</small>
      {state === 'error' && <p id={`${id}-error`} className="newsletter-signup-error" role="alert">{message}</p>}
    </form>}
  </section>
}
